import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth'
import { api, type ClinicSettings } from '../api'
import { useRoleFlags } from '../pages/mobile/shared'
import { Icon, P, initials } from '../pages/platform/ui'
import '../desk.css'

/* The clinic's desktop shell, in TradeDesk's layout: a grouped, collapsible
 * sidebar, a sticky top bar with patient search, accent themes, dark mode and
 * an account menu. Every staff screen renders inside it. */

const THEMES: { id: string; label: string; hex: string }[] = [
  { id: 'indigo', label: 'Indigo', hex: '#6246ea' },
  { id: 'emerald', label: 'Emerald', hex: '#0d9668' },
  { id: 'saffron', label: 'Saffron', hex: '#d96b09' },
  { id: 'rose', label: 'Rose', hex: '#e02e5c' },
  { id: 'ocean', label: 'Ocean', hex: '#0a7bb8' },
  { id: 'graphite', label: 'Graphite', hex: '#424a5e' },
]

function stored(key: string, fallback: string): string {
  try { return localStorage.getItem(key) ?? fallback } catch { return fallback }
}
function store(key: string, value: string) {
  try { localStorage.setItem(key, value) } catch { /* a preference only */ }
}

type Item = { to: string; label: string; icon: string; end?: boolean }

export default function DeskShell({ children }: { children: ReactNode }) {
  const { user, signOut } = useAuth()
  const { canBill } = useRoleFlags()
  const [apptsOn, setApptsOn] = useState(true)
  useEffect(() => {
    void api.get<{ settings: ClinicSettings | null }>('/api/settings')
      .then((r) => setApptsOn(r.settings?.appointmentsEnabled !== false)).catch(() => { /* keep showing them */ })
  }, [])
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [theme, setTheme] = useState(() => stored('cd.theme', 'indigo'))
  const [mode, setMode] = useState(() => stored('cd.mode', 'light'))
  const [collapsed, setCollapsed] = useState(() => stored('cd.collapsed', '0') === '1')
  const [drawer, setDrawer] = useState(false)
  const [menu, setMenu] = useState<null | 'theme' | 'account'>(null)
  const [q, setQ] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const role = user?.role
  const isAdmin = role === 'admin'
  const isClinical = role === 'admin' || role === 'doctor'
  const clinic = user?.clinicName || 'ClinicDesk'

  // The theme lives on <html> so the page background and every screen follow it.
  useEffect(() => {
    const el = document.documentElement
    el.dataset.theme = theme
    el.dataset.mode = mode
    return () => { delete el.dataset.theme; delete el.dataset.mode }
  }, [theme, mode])
  useEffect(() => { setDrawer(false); setMenu(null) }, [pathname])
  useEffect(() => {
    document.body.style.overflow = drawer ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [drawer])
  useEffect(() => {
    const on = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); searchRef.current?.focus() }
    }
    window.addEventListener('keydown', on)
    return () => window.removeEventListener('keydown', on)
  }, [])
  useEffect(() => {
    if (!menu) return
    const close = () => setMenu(null)
    window.addEventListener('click', close)
    return () => window.removeEventListener('click', close)
  }, [menu])

  function search(e: FormEvent) {
    e.preventDefault()
    const term = q.trim()
    navigate(term ? `/reception?q=${encodeURIComponent(term)}` : '/reception')
    setQ('')
  }

  const groups: { label: string; items: Item[] }[] = [
    ...(canBill ? [{ label: 'Billing', items: [
      { to: '/', label: 'New bill', icon: P.plus, end: true },
      { to: '/billing', label: 'Bills', icon: P.rupee },
    ] }] : []),
    { label: 'Front desk', items: [
      ...(canBill ? [] : [{ to: '/', label: 'Overview', icon: P.home, end: true }]),
      { to: '/reception', label: 'Reception', icon: P.desk },
      ...(apptsOn ? [{ to: '/appointments', label: 'Appointments', icon: P.cal }] : []),
      ...(canBill ? [{ to: '/overview', label: 'Overview', icon: P.home }] : []),
    ] },
    { label: 'Clinical', items: [
      ...(isClinical ? [{ to: '/console', label: 'Consulting room', icon: P.steth }] : []),
      { to: '/investigations', label: 'Investigations', icon: P.tube },
      { to: '/records', label: 'Records', icon: P.file },
      ...(isClinical ? [{ to: '/reports', label: 'Reports', icon: P.chart }] : []),
    ] },
    ...(isAdmin ? [{ label: 'Administration', items: [
      { to: '/doctors', label: 'Doctors', icon: P.steth },
      { to: '/users', label: 'Staff logins', icon: P.users },
      { to: '/settings', label: 'Clinic settings', icon: P.settings },
      { to: '/admin', label: 'Admin tools', icon: P.shield },
    ] }] : []),
  ]

  return (
    <div className={`desk${collapsed ? ' collapsed' : ''}${drawer ? ' drawer-open' : ''}`}>
      {drawer && <div className="desk-backdrop" onClick={() => setDrawer(false)} />}
      <aside className="desk-side" aria-label="Main navigation">
        <div className="desk-brand">
          <span className="desk-logo"><Icon d={P.heart} size={17} /></span>
          <div className="desk-brand-text"><b title={clinic}>{clinic}</b><small>Clinic Desk</small></div>
        </div>
        <nav className="desk-nav">
          {groups.map((g) => (
            <div key={g.label}>
              <div className="desk-group">{g.label}</div>
              {g.items.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} title={it.label}
                  className={({ isActive }) => `desk-item${isActive ? ' active' : ''}`}>
                  <Icon d={it.icon} size={17} /><span>{it.label}</span>
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className="desk-side-foot">
          <NavLink to="/m" className="desk-item" title="Mobile app"><Icon d={P.phone} size={17} /><span>Mobile app</span></NavLink>
          <button type="button" className="desk-item desk-collapse" title={collapsed ? 'Expand' : 'Collapse'}
            onClick={() => { const v = !collapsed; setCollapsed(v); store('cd.collapsed', v ? '1' : '0') }}>
            <Icon d={P.collapse} size={17} /><span>{collapsed ? 'Expand' : 'Collapse'}</span>
          </button>
        </div>
      </aside>

      <div className="desk-main">
        <header className="desk-top">
          <button type="button" className="desk-icon-btn desk-only-sm" aria-label="Open menu" aria-expanded={drawer} onClick={() => setDrawer(true)}>
            <Icon d={P.menu} size={18} />
          </button>
          <form className="desk-search" role="search" onSubmit={search}>
            <Icon d={P.search} size={15} />
            <input ref={searchRef} aria-label="Find a patient" placeholder="Find a patient by name, mobile or code" value={q} onChange={(e) => setQ(e.target.value)} />
            <kbd className="desk-hide-sm">⌘K</kbd>
          </form>
          <div className="desk-top-right">
            {canBill && <NavLink to="/" end className="desk-btn desk-hide-sm"><Icon d={P.plus} size={15} />New bill</NavLink>}
            <div className="desk-menu-wrap">
              <button type="button" className="desk-icon-btn" aria-label="Theme colour" aria-expanded={menu === 'theme'}
                onClick={(e) => { e.stopPropagation(); setMenu(menu === 'theme' ? null : 'theme') }}><Icon d={P.palette} size={18} /></button>
              {menu === 'theme' && (
                <div className="desk-menu" onClick={(e) => e.stopPropagation()}>
                  <div className="desk-menu-label">Accent colour</div>
                  <div className="desk-swatches">
                    {THEMES.map((t) => (
                      <button key={t.id} type="button" aria-label={t.label} title={t.label} className={theme === t.id ? 'on' : ''}
                        style={{ background: t.hex }} onClick={() => { setTheme(t.id); store('cd.theme', t.id) }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button type="button" className="desk-icon-btn" aria-label={mode === 'dark' ? 'Light mode' : 'Dark mode'}
              onClick={() => { const m = mode === 'dark' ? 'light' : 'dark'; setMode(m); store('cd.mode', m) }}>
              <Icon d={mode === 'dark' ? P.sun : P.moon} size={18} />
            </button>
            <div className="desk-menu-wrap">
              <button type="button" className="desk-account" aria-expanded={menu === 'account'}
                onClick={(e) => { e.stopPropagation(); setMenu(menu === 'account' ? null : 'account') }}>
                <span className="desk-avatar">{initials(user?.fullName ?? '?')}</span>
                <span className="desk-hide-sm desk-account-text"><b>{user?.fullName}</b><small>{role?.replace('_', ' ')}</small></span>
              </button>
              {menu === 'account' && (
                <div className="desk-menu" onClick={(e) => e.stopPropagation()}>
                  <div style={{ padding: '8px 10px' }}>
                    <div style={{ fontWeight: 600 }}>{user?.fullName}</div>
                    <div style={{ fontSize: 12, color: 'var(--muted)' }}>@{user?.username} · {clinic}</div>
                  </div>
                  <hr />
                  <NavLink to="/change-password"><Icon d={P.key} />Change password</NavLink>
                  <NavLink to="/m"><Icon d={P.phone} />Open the mobile app</NavLink>
                  <hr />
                  <button type="button" onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}><Icon d={P.logout} />Sign out</button>
                </div>
              )}
            </div>
          </div>
        </header>
        <main className="main">{children}</main>
      </div>
    </div>
  )
}
